import type { Address, AddressResponse, SearchAddressResponse } from "../api/address";
import type { ApiResponse } from "../../types/api";
import { addressesStore } from "../stores/addresses";
import { get } from "svelte/store";

export const dummyAddresses = [
  {
    id: 1,
    is_default: true,
    address1: "1234 Main St",
    zip: "12345",
    city: "Springfield",
    state: "IL",
    country: "US",
    latitude: 39.781721,
    longitude: -89.650148,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K8",
    address2: "Apt 101",
    note: "This is a multiline note.\nIt can be as long as you want.\nIt can even contain emojis! 🎉"
  },
  {
    id: 2,
    is_default: false,
    address1: "5678 Elm St",
    zip: "67890",
    city: "Shelbyville",
    state: "KY",
    country: "US",
    latitude: 38.212018,
    longitude: -85.223031,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K9"
  },
  {
    id: 3,
    is_default: false,
    address1: "9101 Oak St",
    zip: "13579",
    city: "Oakland",
    state: "CA",
    country: "US",
    latitude: 37.804363,
    longitude: -122.271111,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K0"
  },
  {
    id: 4,
    is_default: false,
    address1: "1122 Pine St",
    zip: "24680",
    city: "Pineville",
    state: "NC",
    country: "US",
    latitude: 35.083187,
    longitude: -80.892600,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K1"
  },
  {
    id: 5,
    is_default: false,
    address1: "1314 Cedar St",
    zip: "36912",
    city: "Cedarville",
    state: "OH",
    country: "US",
    latitude: 39.761830,
    longitude: -83.808818,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K2"
  },
  {
    id: 6,
    is_default: false,
    address1: "1516 Maple St",
    zip: "57913",
    city: "Mapleton",
    state: "UT",
    country: "US",
    latitude: 40.115006,
    longitude: -111.654923,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K3"
  },
  {
    id: 7,
    is_default: false,
    address1: "1718 Birch St",
    zip: "79135",
    city: "Birchwood",
    state: "WI",
    country: "US",
    latitude: 45.491416,
    longitude: -91.738182,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K4"
  },
  {
    id: 8,
    is_default: false,
    address1: "1920 Ash St",
    zip: "91357",
    city: "Ashland",
    state: "OR",
    country: "US",
    latitude: 42.194576,
    longitude: -122.709477,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K5"
  },
  {
    id: 9,
    is_default: false,
    address1: "2122 Walnut St",
    zip: "13579",
    city: "Walnut Creek",
    state: "CA",
    country: "US",
    latitude: 37.910078,
    longitude: -122.065181,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K6"
  },
  {
    id: 10,
    is_default: false,
    address1: "2324 Chestnut St",
    zip: "24680",
    city: "Chestnut Hill",
    state: "NC",
    country: "US",
    latitude: 35.086803,
    longitude: -80.690097,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K7"
  },
  {
    id: 11,
    is_default: false,
    address1: "2526 Spruce St",
    zip: "36912",
    city: "Spruce Pine",
    state: "OH",
    country: "US",
    latitude: 39.438175,
    longitude: -82.231399,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K8"
  },
  {
    id: 12,
    is_default: false,
    address1: "2728 Fir St",
    zip: "57913",
    city: "Fircrest",
    state: "UT",
    country: "US",
    latitude: 40.579606,
    longitude: -111.893874,
    place_id: "ChIJ7cv00DwsDogRAMDACa2m4K9"
  }
];

export const createAddress = async (address: Address): Promise<AddressResponse> => {
  const currentAddresses = get(addressesStore).addresses;
  const actualAddresses = currentAddresses.length ? currentAddresses : dummyAddresses;

  return Promise.resolve({
    address: {
      ...address,
      id: actualAddresses.length + 1
    },
    success: true
  });
};

export const updateAddress = async (address: Address): Promise<AddressResponse> => {
  const currentAddresses = get(addressesStore).addresses;
  const actualAddresses = currentAddresses.length ? currentAddresses : dummyAddresses;

  return Promise.resolve({
    address: {
      ...address,
      id: actualAddresses.length + 1
    },
    success: true
  });
};

export const setAddressAsDefault = async (): Promise<ApiResponse> => Promise.resolve({ success: true });

export const searchAddress = async (query: string): Promise<SearchAddressResponse> => {
  if (query === "error") {
    return Promise.reject({
      error: "Error searching address",
      success: false
    });
  } else {
    return Promise.resolve({
      addresses: [
        {
          "street_number": "123",
          "route": "William St",
          "city": "New York",
          "state": "New York",
          "zip": "10038-0111",
          "country": "US",
          "latitude": 40.70922,
          "longitude": -74.00689,
          "address1": "123 William St",
          "formatted_address": "123 William St, New York, NY, 10038, US"
        },
        {
          "street_number": "123",
          "route": "William St",
          "city": "Arcade",
          "state": "New York",
          "zip": "14009",
          "country": "US",
          "latitude": 42.52947,
          "longitude": -78.45334,
          "address1": "123 William St",
          "formatted_address": "123 William St, New York, NY, 10038, US"
        },
        {
          "street_number": "123",
          "route": "Arcos",
          "city": "Buenos Aires",
          "state": "Argentina",
          "zip": "14009",
          "country": "AR",
          "latitude": -34.603722,
          "longitude": -58.381592,
          "address1": "123 Arcos",
          "formatted_address": "123 William St, New York, NY, 10038, US"
        }
      ],
      success: true
    }); 
  }
};
